import { ApolloError, useMutation } from '@apollo/client'
import { useMemo, useState } from 'react'
import { CREATE_TASK, SOFT_REMOVE_TASK, UPDATE_TASK } from 'queries/customSchedule'
import { createTask, createTaskVariables } from '__generatedTypes__/createTask'
import { updateTask, updateTaskVariables } from '__generatedTypes__/updateTask'
import { durationToNumber, durationToString } from 'pagesNew/PracticeTests/Form'
import { toast } from 'utils/toast'
import { findAllForCMSTask_findAllForCMSTask_items as Task } from '__generatedTypes__/findAllForCMSTask'
import { softRemoveTask, softRemoveTaskVariables } from '__generatedTypes__/softRemoveTask'
import { UpdateTaskInput } from '__generatedTypes__/globalTypes'
import { FormValues } from '../TaskModal/Form'
import { ALL_SUBJECT_ID } from '../List/useTaskFilters'

export const useTaskModal = (platformId: string, refetch: (task?: Partial<UpdateTaskInput>) => Promise<unknown>) => {
  const [createTask] = useMutation<createTask, createTaskVariables>(CREATE_TASK)
  const [updateTask] = useMutation<updateTask, updateTaskVariables>(UPDATE_TASK)
  const [softRemoveTask] = useMutation<softRemoveTask, softRemoveTaskVariables>(SOFT_REMOVE_TASK)

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [task, setTask] = useState<Task | null>(null)

  const handleOpenTaskModal = (task?: Task) => {
    if (task) {
      setTask(task)
    }

    setIsTaskModalOpen(true)
  }

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false)
    setTask(null)
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      const variables = {
        id: task?.id,
        title: values.title,
        type: values.type,
        subject:
          values.subject === ALL_SUBJECT_ID
            ? null
            : {
                id: values.subject,
              },
        time: values.time ? durationToNumber(values.time) * 60 : 0,
        description: values.description ?? '',
        link: values.link,
        platform: {
          id: platformId,
        },
      }

      if (task) {
        await updateTask({
          variables: {
            updateTaskInputFieldName: {
              ...variables,
            },
          },
        })
      } else {
        await createTask({
          variables: {
            createTaskInput: variables,
          },
        })
      }

      handleCloseTaskModal()
      await refetch(variables)

      toast.success(`Task ${task ? 'edited' : 'created'} successfully`)
    } catch (error) {
      toast.error(`Failed to ${task ? 'edit' : 'create'} task`)
    }
  }

  const handleDeleteTask = async (taskId: string) => {
    if (!taskId) {
      return
    }

    try {
      await softRemoveTask({
        variables: {
          softRemoveTaskId: {
            id: taskId,
          },
        },
      })

      await refetch()
      handleCloseTaskModal()
      toast.success('Task deleted successfully')
    } catch (error) {
      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        return toast.error((error as ApolloError).graphQLErrors?.[0]?.message as string)
      }

      toast.error('Failed to delete task')
    }
  }

  const initialValues: FormValues | undefined = useMemo(() => {
    if (task) {
      return {
        id: task.id,
        title: task.title,
        subject: task.subject ? task.subject?.id : ALL_SUBJECT_ID,
        type: task.type,
        time: task.time ? durationToString(task.time / 60) : undefined,
        description: task.description ?? '',
        link: task.link ?? '',
      }
    }

    return undefined
  }, [task])

  return {
    isOpen: isTaskModalOpen,
    task,
    initialValues,
    onOpen: handleOpenTaskModal,
    onClose: handleCloseTaskModal,
    onSubmit: handleSubmit,
    onDelete: handleDeleteTask,
  }
}
